/**
 * Master Tour Calendar v2 settlement math + import.
 * Presets supply labels and split % only — never invented ticket counts or dollars.
 */

export const DEAL_PRESETS = {
  club: { label: 'Club', terms: 'Guarantee vs 85% of net', split: 85 },
  theatre: { label: 'Theatre', terms: 'Guarantee + 90/10 after costs', split: 90 },
  flat: { label: 'Flat', terms: 'Flat guarantee, no sell-off', split: 0 },
  festival: { label: 'Festival', terms: 'Flat fee, festival covers production', split: 0 },
};

export const EXPENSE_LABELS = {
  club: [
    'Sound & light',
    'Stagehands (5 in / 4 out)',
    'Catering & buyouts',
    'Security',
    'Runner & vans',
    'ASCAP / BMI',
    'Insurance',
    'Ticketing fees',
    'Support act fees',
  ],
  theatre: [
    'Sound & light',
    'Stagehands & rigging',
    'Catering & buyouts',
    'Security',
    'Box office staff',
    'Runner & vans',
    'ASCAP / BMI',
    'Insurance',
    'Ticketing fees',
    'Support act fees',
  ],
  flat: [
    'Sound & light',
    'Stagehands',
    'Catering & buyouts',
    'Security',
    'ASCAP / BMI',
    'Ticketing fees',
  ],
  festival: ['Backline rental', 'Ground transport', 'Catering (buyout)'],
};

export const TIER_LABELS = ['GA advance', 'GA day of show', 'Balcony reserved', 'Comps & guest list'];

export const MERCH_SELLERS = ['House', 'Band', 'Crew', 'Artist', 'Hired'];
export const MERCH_CATEGORIES = ['Apparel', 'Music', 'Other'];
export const MERCH_TYPES = {
  Apparel: ['T-Shirt', 'Long Sleeve', 'Hoodie', 'Sweatshirt', 'Tank', 'Hat', 'Beanie', 'Socks', 'Other apparel'],
  Music: ['CD', 'Vinyl', 'Cassette', 'USB', 'Download card'],
  Other: ['Poster', 'Sticker', 'Patch', 'Pin', 'Tote', 'Other'],
};
export const MERCH_SEX = ['Male', 'Female', 'Unisex'];
export const MERCH_REORDER = ['High', 'Medium', 'Low', 'None'];
export const MERCH_AVAIL = ['Headline', 'Support', 'Festival'];
export const MERCH_COLORS = ['Black', 'White', 'Grey', 'Navy', 'Red', 'Blue', 'Green', 'Cream', 'Pink', 'Yellow'];
export const MERCH_SIZE_GROUPS = {
  Adult: ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', 'One-Size'],
  Youth: ['YXS', 'YS', 'YM', 'YL', 'YXL'],
  'Baby/Toddler': ['6M', '12M', '18M', '24M', '2T', '3T', '4T', '5T'],
  Shoes: ['6', '7', '8', '9', '10', '11', '12', '13'],
};

export function merchUniqLabels(list) {
  const out = [];
  (list || []).forEach((value) => {
    const label = String(value || '').trim();
    if (label && !out.includes(label)) out.push(label);
  });
  return out;
}

export function merchSheetOptions(value = 0) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      merchRate: NUM(value.merchRate),
      musicRate: NUM(value.musicRate),
      taxOnTop: value.taxOnTop === true || value.taxOnTop === 'true' || value.taxOnTop === 1,
      taxRate: NUM(value.taxRate),
      customCategories: merchUniqLabels(value.customCategories),
      customTypes: merchUniqLabels(value.customTypes),
      customSizes: merchUniqLabels(value.customSizes),
      customAvail: merchUniqLabels(value.customAvail),
      customSellers: merchUniqLabels(value.customSellers),
      customColors: merchUniqLabels(value.customColors),
    };
  }
  return {
    merchRate: NUM(value),
    musicRate: 0,
    taxOnTop: false,
    taxRate: 0,
    customCategories: [],
    customTypes: [],
    customSizes: [],
    customAvail: [],
    customSellers: [],
    customColors: [],
  };
}

function emptyMerchRemoved() {
  return {
    categories: [],
    types: [],
    avail: [],
    sellers: [],
    sex: [],
    reorder: [],
    sizes: [],
    colors: [],
  };
}

function merchRemovedKey(field) {
  if (field === 'size') return 'sizes';
  if (field === 'type') return 'types';
  if (field === 'category') return 'categories';
  if (field === 'seller') return 'sellers';
  if (field === 'color') return 'colors';
  return field;
}

function coerceMerchRemoved(raw) {
  const fallback = emptyMerchRemoved();
  const src = raw && typeof raw === 'object' ? raw : {};
  Object.keys(fallback).forEach((key) => {
    fallback[key] = merchUniqLabels(src[key]);
  });
  return fallback;
}

function withoutMerchRemoved(list, removed) {
  const block = new Set(removed || []);
  return (list || []).filter((item) => item && !block.has(item));
}

export function defaultMerchUserOptions() {
  return {
    v: 1,
    categories: merchUniqLabels(MERCH_CATEGORIES),
    types: merchUniqLabels(Object.values(MERCH_TYPES).flat()),
    avail: merchUniqLabels(MERCH_AVAIL),
    sellers: merchUniqLabels(MERCH_SELLERS),
    sex: merchUniqLabels(MERCH_SEX),
    reorder: merchUniqLabels(MERCH_REORDER),
    colors: merchUniqLabels(MERCH_COLORS),
    sizeGroups: {
      Adult: [...(MERCH_SIZE_GROUPS.Adult || [])],
      Youth: [...(MERCH_SIZE_GROUPS.Youth || [])],
      'Baby/Toddler': [...(MERCH_SIZE_GROUPS['Baby/Toddler'] || [])],
      Shoes: [...(MERCH_SIZE_GROUPS.Shoes || [])],
      Custom: [],
    },
    removed: emptyMerchRemoved(),
  };
}

export function coerceMerchUserOptions(raw) {
  const fallback = defaultMerchUserOptions();
  if (!raw || typeof raw !== 'object') return fallback;
  const groups = raw.sizeGroups && typeof raw.sizeGroups === 'object' ? raw.sizeGroups : {};
  const removed = coerceMerchRemoved(raw.removed);
  return {
    v: 1,
    categories: withoutMerchRemoved(
      Array.isArray(raw.categories) ? merchUniqLabels(raw.categories) : fallback.categories,
      removed.categories
    ),
    types: withoutMerchRemoved(
      Array.isArray(raw.types) ? merchUniqLabels(raw.types) : fallback.types,
      removed.types
    ),
    avail: withoutMerchRemoved(
      Array.isArray(raw.avail) ? merchUniqLabels(raw.avail) : fallback.avail,
      removed.avail
    ),
    sellers: withoutMerchRemoved(
      Array.isArray(raw.sellers) ? merchUniqLabels(raw.sellers) : fallback.sellers,
      removed.sellers
    ),
    sex: withoutMerchRemoved(
      Array.isArray(raw.sex) ? merchUniqLabels(raw.sex) : fallback.sex,
      removed.sex
    ),
    reorder: withoutMerchRemoved(
      Array.isArray(raw.reorder) ? merchUniqLabels(raw.reorder) : fallback.reorder,
      removed.reorder
    ),
    colors: withoutMerchRemoved(
      Array.isArray(raw.colors) ? merchUniqLabels(raw.colors) : fallback.colors,
      removed.colors
    ),
    sizeGroups: {
      Adult: withoutMerchRemoved(merchUniqLabels(groups.Adult || fallback.sizeGroups.Adult), removed.sizes),
      Youth: withoutMerchRemoved(merchUniqLabels(groups.Youth || fallback.sizeGroups.Youth), removed.sizes),
      'Baby/Toddler': withoutMerchRemoved(merchUniqLabels(groups['Baby/Toddler'] || fallback.sizeGroups['Baby/Toddler']), removed.sizes),
      Shoes: withoutMerchRemoved(merchUniqLabels(groups.Shoes || fallback.sizeGroups.Shoes), removed.sizes),
      Custom: withoutMerchRemoved(merchUniqLabels(groups.Custom || fallback.sizeGroups.Custom), removed.sizes),
    },
    removed,
  };
}

export function mergeTourCustomsIntoUserOptions(options, sheet = {}) {
  const next = coerceMerchUserOptions(options);
  const customs = merchSheetOptions(sheet);
  return coerceMerchUserOptions({
    ...next,
    categories: [...next.categories, ...customs.customCategories],
    types: [...next.types, ...customs.customTypes],
    avail: [...next.avail, ...customs.customAvail],
    sellers: [...next.sellers, ...customs.customSellers],
    colors: [...next.colors, ...customs.customColors],
    sizeGroups: {
      ...next.sizeGroups,
      Custom: [...(next.sizeGroups.Custom || []), ...customs.customSizes],
    },
    removed: next.removed,
  });
}

export function rewriteMerchUserOptions(options, { field, from, to }) {
  const next = coerceMerchUserOptions(options);
  const src = String(from || '').trim();
  const dest = String(to || '').trim();
  if (!src) return next;
  const key = merchRemovedKey(field);
  next.removed[key] = merchUniqLabels([...(next.removed[key] || []), src]);
  if (dest) {
    next.removed[key] = (next.removed[key] || []).filter((item) => item !== dest);
  }
  const swap = (list) => merchUniqLabels((list || []).map((item) => (item === src ? dest : item)).filter(Boolean));
  if (field === 'category') next.categories = swap(next.categories);
  if (field === 'type') next.types = swap(next.types);
  if (field === 'avail') next.avail = swap(next.avail);
  if (field === 'seller') next.sellers = swap(next.sellers);
  if (field === 'sex') next.sex = swap(next.sex);
  if (field === 'reorder') next.reorder = swap(next.reorder);
  if (field === 'color') next.colors = swap(next.colors);
  if (field === 'size') {
    Object.keys(next.sizeGroups || {}).forEach((group) => {
      next.sizeGroups[group] = swap(next.sizeGroups[group]);
    });
  }
  return coerceMerchUserOptions(next);
}

export function addMerchUserOption(options, { field, label, sizeGroup = 'Custom' }) {
  const next = coerceMerchUserOptions(options);
  const value = String(label || '').trim();
  if (!value) return next;
  const key = merchRemovedKey(field);
  next.removed[key] = (next.removed[key] || []).filter((item) => item !== value);
  if (field === 'category') next.categories = merchUniqLabels([...next.categories, value]);
  if (field === 'type') next.types = merchUniqLabels([...next.types, value]);
  if (field === 'avail') next.avail = merchUniqLabels([...next.avail, value]);
  if (field === 'seller') next.sellers = merchUniqLabels([...next.sellers, value]);
  if (field === 'sex') next.sex = merchUniqLabels([...next.sex, value]);
  if (field === 'reorder') next.reorder = merchUniqLabels([...next.reorder, value]);
  if (field === 'color') next.colors = merchUniqLabels([...next.colors, value]);
  if (field === 'size') {
    const group = next.sizeGroups[sizeGroup] ? sizeGroup : 'Custom';
    next.sizeGroups[group] = merchUniqLabels([...(next.sizeGroups[group] || []), value]);
  }
  return coerceMerchUserOptions(next);
}

export function merchDefaultTypeLabels() {
  return merchUniqLabels(Object.values(MERCH_TYPES).flat());
}

export function merchDefaultSizeLabels() {
  return merchUniqLabels(Object.values(MERCH_SIZE_GROUPS).flat());
}

export function merchTypesForCategory(options, category) {
  const o = coerceMerchUserOptions(options);
  const defaults = MERCH_TYPES[category] || MERCH_TYPES.Apparel || [];
  const allDefaults = merchDefaultTypeLabels();
  return merchUniqLabels([
    ...defaults.filter((typ) => o.types.includes(typ)),
    ...o.types.filter((typ) => !allDefaults.includes(typ)),
  ]);
}

export function merchSheetCustomsFromUserOptions(options) {
  const o = coerceMerchUserOptions(options);
  const defaultTypes = merchDefaultTypeLabels();
  const defaultSizes = merchDefaultSizeLabels();
  return {
    customCategories: (o.categories || []).filter((item) => !MERCH_CATEGORIES.includes(item)),
    customTypes: (o.types || []).filter((item) => !defaultTypes.includes(item)),
    customSizes: merchUniqLabels(Object.values(o.sizeGroups || {}).flat()).filter((item) => !defaultSizes.includes(item)),
    customAvail: (o.avail || []).filter((item) => !MERCH_AVAIL.includes(item)),
    customSellers: (o.sellers || []).filter((item) => !MERCH_SELLERS.includes(item)),
    customColors: (o.colors || []).filter((item) => !MERCH_COLORS.includes(item)),
  };
}

function swapMerchLabels(list, from, to) {
  const src = String(from || '').trim();
  const dest = String(to || '').trim();
  return merchUniqLabels((list || []).map((item) => (item === src ? dest : item)).filter(Boolean));
}

export function rewriteMerchSheetCustoms(sheet, { field, from, to } = {}) {
  const opts = merchSheetOptions(sheet);
  const src = String(from || '').trim();
  if (!src) return opts;
  if (field === 'category') opts.customCategories = swapMerchLabels(opts.customCategories, from, to);
  if (field === 'type') opts.customTypes = swapMerchLabels(opts.customTypes, from, to);
  if (field === 'size') opts.customSizes = swapMerchLabels(opts.customSizes, from, to);
  if (field === 'avail') opts.customAvail = swapMerchLabels(opts.customAvail, from, to);
  if (field === 'seller') opts.customSellers = swapMerchLabels(opts.customSellers, from, to);
  if (field === 'color') opts.customColors = swapMerchLabels(opts.customColors, from, to);
  return opts;
}

export function rewriteMerchRowsOption(rows, { field, from, to }) {
  const src = String(from || '').trim();
  const dest = String(to || '').trim();
  if (!src) return (rows || []).map((row, i) => normalizeMerchRow(row, i));
  return (rows || []).map((row, i) => {
    const next = { ...normalizeMerchRow(row, i) };
    if (field === 'category' && next.category === src) {
      next.category = dest;
      next.kind = dest;
    }
    if (field === 'type' && next.type === src) next.type = dest;
    if (field === 'seller' && next.seller === src) next.seller = dest;
    if (field === 'sex' && next.sex === src) next.sex = dest;
    if (field === 'reorder' && next.reorder === src) next.reorder = dest;
    if (field === 'avail') {
      next.available = swapMerchLabels(next.available, from, to);
    }
    if (field === 'color') {
      next.colors = swapMerchLabels(next.colors, from, to);
      next.sizes = (next.sizes || [])
        .map((size) => (size.color === src ? (dest ? { ...size, color: dest } : null) : size))
        .filter(Boolean);
    }
    if (field === 'size') {
      next.sizes = (next.sizes || [])
        .map((size) => (size.size === src ? (dest ? { ...size, size: dest } : null) : size))
        .filter(Boolean);
    }
    return normalizeMerchRow(next, i);
  });
}

export function rewriteMerchPayload(raw, change) {
  if (raw == null) return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.stringify(rewriteMerchPayload(JSON.parse(raw), change));
    } catch {
      return raw;
    }
  }
  if (Array.isArray(raw)) return rewriteMerchRowsOption(raw, change);
  const customs = rewriteMerchSheetCustoms(raw, change);
  const next = { ...raw, ...customs };
  if (Array.isArray(raw.rows)) next.rows = rewriteMerchRowsOption(raw.rows, change);
  if (Array.isArray(raw.items)) next.items = rewriteMerchRowsOption(raw.items, change);
  if (Array.isArray(raw.songs)) {
    next.songs = raw.songs.map((song, i) => {
      if (!song?.item) return song;
      const item = rewriteMerchRowsOption([song.item], change)[0];
      return { ...song, item, title: item.name || song.title };
    });
    if (!Array.isArray(raw.rows)) {
      next.rows = rewriteMerchRowsOption(raw.songs.map((song, i) => (
        song?.item ? { ...song.item, id: song.id || song.item.id, name: song.title || song.item.name } : parseMerchCatalogItem(song, i)
      )), change);
    }
  }
  return next;
}

export function emptyMerchSize(partial = {}) {
  return {
    size: String(partial.size || ''),
    color: String(partial.color || ''),
    sku: String(partial.sku || ''),
    price: NUM(partial.price),
    cogs: NUM(partial.cogs),
    qtyAvail: NUM(partial.qtyAvail),
    countIn: NUM(partial.countIn),
    countAdd: NUM(partial.countAdd),
    countOut: NUM(partial.countOut),
    countComp: NUM(partial.countComp),
    enabled: partial.enabled !== false,
  };
}

export function merchColorList(value) {
  if (Array.isArray(value)) return merchUniqLabels(value);
  return merchUniqLabels(String(value || '').split(/[,/|]/));
}

export function merchVariantKey(variant) {
  return `${String(variant?.color || '').trim()}::${String(variant?.size || '').trim()}`;
}

export function merchVariantLabel(variant) {
  const color = String(variant?.color || '').trim();
  const size = String(variant?.size || '').trim();
  if (color && size) return `${color} / ${size}`;
  return color || size || '';
}

export function merchSelectedSizeNames(row) {
  return merchUniqLabels((row?.sizes || []).filter((size) => size && size.enabled !== false).map((size) => size.size));
}

export function toggleMerchSize(row, sizeName) {
  const name = String(sizeName || '').trim();
  if (!name) return normalizeMerchRow(row);
  const colors = merchColorList(row?.colors);
  const sizes = Array.isArray(row?.sizes) ? row.sizes.map((size) => emptyMerchSize(size)) : [];
  const anyOn = sizes.some((size) => size.size === name && size.enabled !== false);
  if (anyOn) {
    return emptyMerchRow({
      ...row,
      sizes: sizes.map((size) => (size.size === name ? { ...size, enabled: false } : size)),
    });
  }
  const colorSlots = colors.length ? colors : [''];
  const next = sizes.slice();
  colorSlots.forEach((color) => {
    const idx = next.findIndex((size) => size.size === name && String(size.color || '') === color);
    if (idx >= 0) next[idx] = { ...next[idx], enabled: true };
    else {
      next.push(emptyMerchSize({
        size: name,
        color,
        price: NUM(row?.price),
        cogs: NUM(row?.cogs),
        enabled: true,
      }));
    }
  });
  return emptyMerchRow({ ...row, sizes: next });
}

export function toggleMerchColor(row, colorName) {
  const name = String(colorName || '').trim();
  if (!name) return normalizeMerchRow(row);
  const selected = merchColorList(row?.colors);
  const on = selected.includes(name);
  const nextColors = on ? selected.filter((item) => item !== name) : merchUniqLabels([...selected, name]);
  let sizes = Array.isArray(row?.sizes) ? row.sizes.map((size) => emptyMerchSize(size)) : [];
  const sizeNames = merchSelectedSizeNames(row);
  if (on) {
    sizes = sizes.map((size) => (String(size.color || '') === name ? { ...size, enabled: false } : size));
  } else if (sizeNames.length) {
    if (!selected.length) {
      sizes = sizes.map((size) => (
        size.enabled !== false && size.size && !String(size.color || '').trim()
          ? { ...size, color: name }
          : size
      ));
    }
    sizeNames.forEach((sizeName) => {
      const idx = sizes.findIndex((size) => size.size === sizeName && String(size.color || '') === name);
      if (idx >= 0) sizes[idx] = { ...sizes[idx], enabled: true };
      else {
        sizes.push(emptyMerchSize({
          size: sizeName,
          color: name,
          price: NUM(row?.price),
          cogs: NUM(row?.cogs),
          enabled: true,
        }));
      }
    });
  } else {
    const idx = sizes.findIndex((size) => String(size.color || '') === name && !String(size.size || '').trim());
    if (idx >= 0) sizes[idx] = { ...sizes[idx], enabled: true };
    else {
      sizes.push(emptyMerchSize({
        color: name,
        size: '',
        price: NUM(row?.price),
        cogs: NUM(row?.cogs),
        enabled: true,
      }));
    }
  }
  return emptyMerchRow({ ...row, colors: nextColors, sizes });
}

function merchAvailableList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v || '').trim()).filter(Boolean);
  return String(value || '').split(',').map((v) => v.trim()).filter(Boolean);
}

export function merchActiveSizes(row) {
  const sizes = Array.isArray(row?.sizes)
    ? row.sizes
      .filter((z) => z && z.enabled !== false && (String(z.size || '').trim() || String(z.color || '').trim()))
      .map((z) => emptyMerchSize(z))
    : [];
  return sizes.length ? sizes : null;
}

export function merchIsMusic(row) {
  return String(row?.category || row?.kind || '').trim().toLowerCase() === 'music';
}

export function merchUnitPrice(row, size) {
  if (size && NUM(size.price)) return NUM(size.price);
  return NUM(row?.price);
}

export function merchSizeSold(size) {
  return Math.max(0, NUM(size?.countIn) + NUM(size?.countAdd) - NUM(size?.countOut) - NUM(size?.countComp));
}

/** Sold units: Count In + Adds − Count Out − Comps (atVenu Counts math, no register). */
export function merchSoldCount(row) {
  const sizes = merchActiveSizes(row);
  if (sizes) return sizes.reduce((sum, size) => sum + merchSizeSold(size), 0);
  return Math.max(0, merchTotalIn(row) - NUM(row?.countOut) - NUM(row?.countComp));
}

export function merchTotalIn(row) {
  const sizes = merchActiveSizes(row);
  if (sizes) return sizes.reduce((sum, size) => sum + NUM(size.countIn) + NUM(size.countAdd), 0);
  return NUM(row?.countIn) + NUM(row?.countAdd);
}

export function merchInvRemaining(row) {
  const sizes = merchActiveSizes(row);
  if (sizes) {
    return sizes.reduce((sum, size) => sum + Math.max(0, NUM(size.countIn) + NUM(size.countAdd) - NUM(size.countComp)), 0);
  }
  return Math.max(0, merchTotalIn(row) - NUM(row?.countComp));
}

export function merchItemGrossIn(row) {
  const sizes = merchActiveSizes(row);
  if (sizes) {
    return sizes.reduce((sum, size) => sum + (NUM(size.countIn) + NUM(size.countAdd)) * merchUnitPrice(row, size), 0);
  }
  return merchTotalIn(row) * NUM(row?.price);
}

export function merchLineGross(row) {
  const sizes = merchActiveSizes(row);
  if (sizes) {
    return sizes.reduce((sum, size) => sum + merchSizeSold(size) * merchUnitPrice(row, size), 0);
  }
  return merchSoldCount(row) * NUM(row?.price);
}

export function merchSheetGross(rows) {
  return (rows || []).reduce((sum, row) => sum + merchLineGross(row), 0);
}

export function merchSheetGrossSplit(rows) {
  return (rows || []).reduce((acc, row) => {
    const gross = merchLineGross(row);
    if (merchIsMusic(row)) acc.music += gross;
    else acc.merch += gross;
    acc.total += gross;
    return acc;
  }, { merch: 0, music: 0, total: 0 });
}

export function merchSheetNet(rows, merchRate = 0, musicRate = 0) {
  const split = merchSheetGrossSplit(rows);
  return split.merch * (1 - NUM(merchRate) / 100) + split.music * (1 - NUM(musicRate) / 100);
}

export function emptyMerchRow(partial = {}) {
  const kindRaw = String(partial.kind || '').trim();
  const requested = String(partial.category || '').trim();
  const category = requested || (MERCH_CATEGORIES.includes(kindRaw) ? kindRaw : '');
  const type = String(partial.type || '').trim()
    || (kindRaw && !MERCH_CATEGORIES.includes(kindRaw) ? kindRaw : '');
  const colors = merchColorList(partial.colors != null ? partial.colors : partial.color);
  return {
    id: String(partial.id || `m-${Date.now()}`),
    name: String(partial.name || ''),
    kind: category || 'Apparel',
    category,
    type,
    sex: String(partial.sex || '').trim(),
    colors,
    color: colors.join(', '),
    sku: String(partial.sku || ''),
    cogs: NUM(partial.cogs),
    reorder: String(partial.reorder || '').trim(),
    alertShows: NUM(partial.alertShows),
    available: merchAvailableList(partial.available),
    price: NUM(partial.price),
    qtyAvail: NUM(partial.qtyAvail),
    countIn: NUM(partial.countIn),
    countAdd: NUM(partial.countAdd),
    countOut: NUM(partial.countOut),
    countComp: NUM(partial.countComp),
    trailerComp: NUM(partial.trailerComp),
    crewQty: NUM(partial.crewQty),
    crewAmount: NUM(partial.crewAmount),
    taxOverride: NUM(partial.taxOverride),
    cutOverride: NUM(partial.cutOverride),
    seller: Object.prototype.hasOwnProperty.call(partial, 'seller')
      ? String(partial.seller || '').trim()
      : 'Band',
    photoUrl: String(partial.photoUrl || ''),
    sizes: Array.isArray(partial.sizes) ? partial.sizes.map((size) => emptyMerchSize(size)) : [],
  };
}

export function normalizeMerchRow(row, index = 0) {
  return emptyMerchRow({ ...row, id: row?.id || `m-${index}` });
}

function merchRowHasCounts(row) {
  if (NUM(row?.countIn) || NUM(row?.countAdd) || NUM(row?.countOut) || NUM(row?.countComp)) return true;
  return (row?.sizes || []).some((size) => (
    NUM(size?.countIn) || NUM(size?.countAdd) || NUM(size?.countOut) || NUM(size?.countComp)
  ));
}

export function merchCountsAreEmpty(rows) {
  return !(rows || []).some((row) => merchRowHasCounts(row));
}

export function previousShowKey(showKey, showDateKeys) {
  const keys = (showDateKeys || []).filter(Boolean).slice().sort();
  const before = keys.filter((key) => key < String(showKey || ''));
  return before.length ? before[before.length - 1] : '';
}

export function merchCatalogItemsFromRows(rows) {
  return (rows || [])
    .filter((row) => String(row?.name || '').trim() || String(row?.photoUrl || '').trim())
    .map((row, i) => {
      const n = normalizeMerchRow(row, i);
      return {
        id: n.id,
        name: n.name,
        kind: n.category,
        category: n.category,
        type: n.type,
        sex: n.sex,
        color: n.color,
        colors: n.colors,
        sku: n.sku,
        cogs: n.cogs,
        reorder: n.reorder,
        alertShows: n.alertShows,
        available: n.available,
        price: n.price,
        seller: n.seller,
        photoUrl: n.photoUrl,
        sizes: (n.sizes || [])
          .filter((size) => size.enabled !== false && (String(size.size || '').trim() || String(size.color || '').trim()))
          .map((size) => ({
            size: size.size,
            color: size.color,
            sku: size.sku,
            price: size.price,
            cogs: size.cogs,
            enabled: true,
          })),
      };
    });
}

export function serializeMerchCatalog(rows, merchRate = 0) {
  const opts = merchSheetOptions(merchRate);
  return JSON.stringify({
    v: 1,
    ...opts,
    items: merchCatalogItemsFromRows(rows),
  });
}

export function parseMerchCatalog(raw) {
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return { items: [], ...merchSheetOptions() };
    try {
      return parseMerchCatalog(JSON.parse(text));
    } catch {
      return { items: [], ...merchSheetOptions() };
    }
  }
  if (raw && Array.isArray(raw.items)) {
    return {
      items: raw.items.map((row, i) => {
        const n = normalizeMerchRow(row, i);
        return merchCatalogItemsFromRows([n])[0] || {
          id: n.id,
          name: n.name,
          kind: n.category,
          category: n.category,
          type: n.type,
          sex: n.sex,
          color: n.color,
          sku: n.sku,
          cogs: n.cogs,
          reorder: n.reorder,
          alertShows: n.alertShows,
          available: n.available,
          price: n.price,
          seller: n.seller,
          photoUrl: n.photoUrl,
          sizes: [],
        };
      }).filter(Boolean),
      ...merchSheetOptions(raw),
    };
  }
  const sheet = parseMerchSheet(raw);
  return { items: merchCatalogItemsFromRows(sheet.rows), ...merchSheetOptions(sheet) };
}

export function coerceMerchStore(value) {
  if (Array.isArray(value)) return { rows: value.map((row, i) => normalizeMerchRow(row, i)), ...merchSheetOptions() };
  if (value && Array.isArray(value.rows)) {
    return { rows: value.rows.map((row, i) => normalizeMerchRow(row, i)), ...merchSheetOptions(value) };
  }
  if (typeof value === 'string') return parseMerchSheet(value);
  return { rows: [], ...merchSheetOptions() };
}

export function parseMerchSheet(raw) {
  if (Array.isArray(raw)) return { rows: raw.map((row, i) => normalizeMerchRow(row, i)), ...merchSheetOptions() };
  if (raw && typeof raw === 'object' && Array.isArray(raw.rows)) {
    return { rows: raw.rows.map((row, i) => normalizeMerchRow(row, i)), ...merchSheetOptions(raw) };
  }
  if (raw && typeof raw === 'object' && Array.isArray(raw.songs)) {
    return {
      rows: raw.songs.map((song, i) => parseMerchCatalogItem(song, i)),
      ...merchSheetOptions(raw),
    };
  }
  const text = String(raw ?? '').trim();
  if (!text) return { rows: [], ...merchSheetOptions() };
  try {
    return parseMerchSheet(JSON.parse(text));
  } catch {
    return { rows: [], ...merchSheetOptions() };
  }
}

function carryMerchSizes(last, cat) {
  const lastSizes = merchActiveSizes(last) || [];
  const catSizes = merchActiveSizes(cat) || [];
  const keys = [];
  const remember = (variant) => {
    const key = merchVariantKey(variant);
    if (key !== '::' && !keys.includes(key)) keys.push(key);
  };
  catSizes.forEach(remember);
  lastSizes.forEach(remember);
  return keys.map((key) => {
    const fromCat = catSizes.find((size) => merchVariantKey(size) === key) || {};
    const fromLast = lastSizes.find((size) => merchVariantKey(size) === key);
    return emptyMerchSize({
      ...fromCat,
      size: fromCat.size || fromLast?.size || '',
      color: fromCat.color || fromLast?.color || '',
      sku: fromCat.sku || fromLast?.sku || '',
      price: NUM(fromCat.price) || NUM(fromLast?.price),
      cogs: NUM(fromCat.cogs) || NUM(fromLast?.cogs),
      qtyAvail: NUM(fromLast?.countOut) || NUM(fromCat.qtyAvail),
      countIn: fromLast ? NUM(fromLast.countOut) : 0,
      countAdd: 0,
      countOut: 0,
      countComp: 0,
      enabled: true,
    });
  });
}

export function carryOverMerchRows(previousRows, catalogItems = []) {
  const prev = (previousRows || []).map((row, i) => normalizeMerchRow(row, i));
  const catalog = (catalogItems || []).map((row, i) => normalizeMerchRow(row, i));
  const ids = [];
  catalog.forEach((item) => { if (item?.id && !ids.includes(item.id)) ids.push(item.id); });
  prev.forEach((row) => { if (row.id && !ids.includes(row.id)) ids.push(row.id); });
  if (!ids.length) return [];
  return ids.map((id) => {
    const cat = catalog.find((item) => item.id === id) || {};
    const last = prev.find((row) => row.id === id);
    const sizes = carryMerchSizes(last, cat);
    return emptyMerchRow({
      ...(last || {}),
      ...cat,
      id,
      name: cat.name || last?.name || '',
      price: cat.price != null && cat.price !== '' ? cat.price : last?.price,
      seller: cat.seller || last?.seller || 'Band',
      photoUrl: cat.photoUrl || last?.photoUrl || '',
      colors: merchColorList(cat.colors || last?.colors || cat.color || last?.color),
      sizes,
      countIn: sizes.length ? 0 : (last ? NUM(last.countOut) : 0),
      countAdd: 0,
      countOut: 0,
      countComp: 0,
      trailerComp: 0,
      crewQty: 0,
      crewAmount: 0,
    });
  });
}

export function mergeMerchNight({ catalogItems = [], nightRows = [], previousRows = [] }) {
  const night = (nightRows || []).map((row, i) => normalizeMerchRow(row, i));
  if (night.length && !merchCountsAreEmpty(night)) return night;
  const carried = carryOverMerchRows(previousRows, catalogItems);
  if (carried.length) return carried;
  if ((catalogItems || []).length) {
    return catalogItems.map((item, i) => emptyMerchRow({ ...item, id: item.id || `m-${i}` }));
  }
  return night;
}

export function NUM(v) {
  const n = Number(String(v ?? '').replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Keep a typed money or hours value to digits, one decimal, and an optional leading minus. */
export function numericText(text) {
  const raw = String(text ?? '');
  const neg = raw.trim().startsWith('-');
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  const body = rest.length ? `${whole}.${rest.join('')}` : whole;
  return neg ? `-${body}` : body;
}

export function formatUsd(n) {
  const v = NUM(n);
  const neg = v < 0;
  return `${neg ? '−$' : '$'}${Math.round(Math.abs(v)).toLocaleString('en-US')}`;
}

export function emptySettle(preset = 'club') {
  const key = DEAL_PRESETS[preset] ? preset : 'club';
  const p = DEAL_PRESETS[key];
  return {
    preset: key,
    guarantee: 0,
    split: p.split,
    deposit: 0,
    facility: 0,
    tax: 0,
    card: 0,
    merchGross: 0,
    merchRate: 0,
    notes: '',
    tiers: TIER_LABELS.map((label) => ({ label, qty: 0, price: 0 })),
    expenses: (EXPENSE_LABELS[key] || EXPENSE_LABELS.club).map((label) => ({ label, amount: 0 })),
  };
}

export function settleTotals(s) {
  const st = s && typeof s === 'object' ? s : emptySettle();
  const tiers = Array.isArray(st.tiers) ? st.tiers : [];
  const expenses = Array.isArray(st.expenses) ? st.expenses : [];
  const paid = tiers.reduce((a, t) => a + (NUM(t.price) > 0 ? NUM(t.qty) : 0), 0);
  const comps = tiers.reduce((a, t) => a + (NUM(t.price) <= 0 ? NUM(t.qty) : 0), 0);
  const gross = tiers.reduce((a, t) => a + NUM(t.qty) * NUM(t.price), 0);
  const potential = tiers.reduce((a, t) => a + Math.max(NUM(t.avail), NUM(t.qty)) * NUM(t.price), 0);
  const facility = paid * NUM(st.facility);
  const tax = (gross * NUM(st.tax)) / 100;
  const card = (gross * NUM(st.card)) / 100;
  const net = gross - facility - tax - card;
  const expenseTotal = expenses.reduce((a, e) => a + NUM(e.amount), 0);
  const afterCosts = net - expenseTotal;
  const gtee = NUM(st.guarantee);
  const promoterProfit = NUM(st.promoterProfit);
  const splitPoint = promoterProfit > 0 ? gtee + expenseTotal + promoterProfit : 0;
  const splitEarn = promoterProfit > 0
    ? (Math.max(0, net - splitPoint) * NUM(st.split)) / 100
    : (afterCosts > 0 ? (afterCosts * NUM(st.split)) / 100 : 0);
  const earned = promoterProfit > 0 ? gtee + splitEarn : Math.max(gtee, splitEarn);
  const merchNet = NUM(st.merchGross) * (1 - NUM(st.merchRate) / 100);
  const deposit = NUM(st.deposit);
  const payments = Array.isArray(st.payments) ? st.payments : [];
  const paymentsTotal = payments.reduce((a, p) => a + NUM(p.amount), 0);
  const paidToDate = deposit + paymentsTotal;
  const balance = earned - paidToDate;
  const disputed = expenses.filter((e) => e.status === 'disputed');
  return {
    paid,
    comps,
    gross,
    potential,
    facility,
    tax,
    card,
    net,
    expenses: expenseTotal,
    afterCosts,
    splitEarn,
    gtee,
    earned,
    overage: Math.max(0, earned - gtee),
    onSplit: promoterProfit > 0 ? splitEarn > 0 : splitEarn > gtee,
    splitPoint,
    paymentsTotal,
    paidToDate,
    disputedCount: disputed.length,
    disputedTotal: disputed.reduce((a, e) => a + NUM(e.amount), 0),
    balance,
    merchNet,
    walkout: balance + merchNet,
  };
}

export function parseAmountCells(cells) {
  const out = [];
  (cells || []).forEach((c) => {
    const raw = String(c ?? '').trim();
    if (!raw) return;
    const n = parseFloat(raw.replace(/[^0-9.\-]/g, ''));
    const looksNumeric = /\d/.test(raw) && Number.isFinite(n) && /^[^a-z]*$/i.test(raw.replace(/[a-z]{1,3}$/i, ''));
    if (looksNumeric) out.push(n);
  });
  return out;
}

/** Split a CSV/TSV/plaintext row into cells. */
export function splitSheetLine(line) {
  const s = String(line || '');
  if (s.includes('\t')) return s.split('\t').map((c) => c.trim());
  const cells = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '"') {
      q = !q;
    } else if ((ch === ',' || ch === ';') && !q) {
      cells.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
}

export function parseSheetRows(rows) {
  const out = { income: [], expense: [] };
  (rows || []).forEach((raw) => {
    if (!raw || !raw.length) return;
    const cells = raw.map((c) => (c == null ? '' : String(c).trim()));
    const joined = cells.join(' ').toLowerCase();
    if (/^(type|category)?\s*(description|item|line|label)/.test(joined) && !/\d/.test(joined)) return;
    let amount = null;
    let label = '';
    let side = null;
    cells.forEach((c) => {
      const n = parseFloat(c.replace(/[^0-9.\-]/g, ''));
      const looksNumeric = c !== '' && /\d/.test(c) && Number.isFinite(n) && /^[^a-z]*$/i.test(c.replace(/[a-z]{1,3}$/i, ''));
      if (looksNumeric && amount === null) amount = n;
      else if (/^(income|revenue|in)$/i.test(c)) side = 'income';
      else if (/^(expense|expenses|cost|out)$/i.test(c)) side = 'expense';
      else if (c && !label) label = c;
    });
    if (!label || amount === null) return;
    if (!side) {
      side = /guarantee|merch|buy-on|buyon|settlement income|deposit received/i.test(label)
        ? 'income'
        : 'expense';
    }
    out[side].push({ label, amount: Math.abs(amount) });
  });
  return out;
}

export function linesFromSheetText(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => {
      const cells = splitSheetLine(line);
      let label = '';
      const nums = [];
      cells.forEach((c) => {
        const n = parseFloat(String(c).replace(/[^0-9.\-]/g, ''));
        const looksNumeric =
          c !== '' && /\d/.test(c) && Number.isFinite(n) && /^[^a-z]*$/i.test(String(c).replace(/[a-z]{1,3}$/i, ''));
        if (looksNumeric) nums.push(n);
        else if (c && !label) label = c;
      });
      return { label, nums };
    })
    .filter((ln) => ln.label && ln.nums.length);
}

export function applySettleLines(current, lines) {
  const st = { ...emptySettle(current?.preset), ...(current || {}) };
  st.tiers = (st.tiers || []).map((t) => ({ ...t }));
  st.expenses = (st.expenses || []).map((e) => ({ ...e }));
  const patch = {};
  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  let fields = 0;
  let exp = 0;
  let tierHits = 0;

  (lines || []).forEach((ln) => {
    const label = String(ln.label || '').trim();
    const nums = ln.nums || [];
    if (!label || !nums.length) return;
    const k = norm(label);
    const last = nums[nums.length - 1];

    if (/guarantee/.test(k)) {
      patch.guarantee = last;
      fields += 1;
      return;
    }
    if (/deposit/.test(k)) {
      patch.deposit = last;
      fields += 1;
      return;
    }
    if (/^(split|splitpct|backend|selloff)$/.test(k)) {
      patch.split = last;
      fields += 1;
      return;
    }
    if (/facilityfee|facilitfee|^facility$/.test(k)) {
      patch.facility = nums[0];
      fields += 1;
      return;
    }
    if (/^(tax|salestax)$/.test(k)) {
      patch.tax = nums[0];
      fields += 1;
      return;
    }
    if (/(creditcard|ccfee|cardfee)/.test(k)) {
      patch.card = nums[0];
      fields += 1;
      return;
    }

    let matched = false;
    for (let i = 0; i < st.tiers.length; i += 1) {
      const tk = norm(st.tiers[i].label);
      if (tk && (tk === k || tk.indexOf(k) > -1 || k.indexOf(tk) > -1)) {
        if (nums.length >= 2) {
          st.tiers[i].qty = nums[0];
          st.tiers[i].price = nums[1];
        } else {
          st.tiers[i].qty = nums[0];
        }
        tierHits += 1;
        matched = true;
        break;
      }
    }
    if (matched) return;

    for (let i = 0; i < st.expenses.length; i += 1) {
      const ek = norm(st.expenses[i].label);
      if (ek && (ek === k || ek.indexOf(k) > -1 || k.indexOf(ek) > -1)) {
        st.expenses[i].amount = last;
        exp += 1;
        matched = true;
        break;
      }
    }
    if (!matched) {
      st.expenses.push({ label, amount: last });
      exp += 1;
    }
  });

  return {
    next: { ...st, ...patch, tiers: st.tiers, expenses: st.expenses },
    fields,
    exp,
    tierHits,
    ok: Boolean(fields || exp || tierHits),
  };
}

export function emptyLedger() {
  return { income: [], expense: [] };
}

export const PER_DIEM_RATE_TYPES = {
  hourly: 'hourly',
  day: 'day',
  fixed: 'fixed',
};

export const PER_DIEM_SPANS = {
  this: 'this',
  all: 'all',
  dates: 'dates',
};

function newRepeatId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Line item: hourly, day rate, or fixed total, and it can cover this day, every day, or picked days. */
export function emptyExpenseLine(dateKey, repeatId) {
  return {
    label: '',
    kind: 'line',
    amount: 0,
    rateType: PER_DIEM_RATE_TYPES.fixed,
    rate: 0,
    hours: 8,
    span: PER_DIEM_SPANS.this,
    dates: dateKey ? [dateKey] : [],
    repeatId: repeatId || newRepeatId('ln'),
  };
}

/** Per diem is only a fixed total. Rate type and day span stay off this row. */
export function emptyPerDiem(dateKey, repeatId) {
  return {
    label: 'Per Diem',
    kind: 'perdiem',
    amount: 0,
    rateType: PER_DIEM_RATE_TYPES.fixed,
    rate: 0,
    hours: 0,
    people: 1,
    span: PER_DIEM_SPANS.this,
    dates: dateKey ? [dateKey] : [],
    repeatId: repeatId || newRepeatId('pd'),
  };
}

/** Day rate is a fixed rate. Which days it covers is chosen on the row. */
export function emptyDayRate(dateKey, repeatId) {
  return {
    label: 'Day rate',
    kind: 'dayrate',
    amount: 0,
    rateType: PER_DIEM_RATE_TYPES.day,
    rate: 0,
    hours: 0,
    span: PER_DIEM_SPANS.this,
    dates: dateKey ? [dateKey] : [],
    repeatId: repeatId || newRepeatId('dr'),
  };
}

function ratedAmount(row) {
  const type = row?.rateType || PER_DIEM_RATE_TYPES.fixed;
  if (type === PER_DIEM_RATE_TYPES.hourly) return NUM(row.rate) * NUM(row.hours);
  if (type === PER_DIEM_RATE_TYPES.day) return NUM(row.rate);
  if (row?.rate != null && String(row.rate) !== '') return NUM(row.rate);
  return NUM(row?.amount);
}

export function perDiemBase(row) {
  if (!row) return 0;
  if (row.kind === 'perdiem') {
    return row.amount != null && String(row.amount) !== '' ? NUM(row.amount) : NUM(row.rate);
  }
  return perDiemAmount({ ...row, people: 1 });
}

export function perDiemAmount(row) {
  if (!row) return 0;
  if (row.kind === 'perdiem') {
    const baseAmount = row.amount != null && String(row.amount) !== '' ? NUM(row.amount) : NUM(row.rate);
    return baseAmount * (Number(row.people) || 1);
  }
  if (row.kind === 'dayrate') {
    if (row.rate != null && String(row.rate) !== '') return NUM(row.rate);
    return NUM(row.amount);
  }
  if (row.kind === 'line') return ratedAmount(row);
  return NUM(row.amount);
}

export function resolvePerDiemDates(row, dateKey, tourDateKeys) {
  const span = row?.span || PER_DIEM_SPANS.this;
  const keys = [...new Set((tourDateKeys || []).filter(Boolean))].sort();
  const anchor = dateKey || '';
  if (span === PER_DIEM_SPANS.all) return keys.length ? keys : (anchor ? [anchor] : []);
  if (span === PER_DIEM_SPANS.dates) {
    const picked = (row?.dates || []).filter(Boolean);
    const allowed = new Set(keys.length ? keys : picked);
    const next = picked.filter((d) => allowed.has(d));
    if (anchor && !next.includes(anchor)) next.push(anchor);
    return [...new Set(next)].sort();
  }
  return anchor ? [anchor] : [];
}

export function upsertPerDiemRow(ledger, row) {
  const expense = [...(ledger?.expense || [])];
  const stored = { ...row, amount: perDiemBase(row) };
  const idx = expense.findIndex((r) => r?.repeatId && r.repeatId === stored.repeatId);
  if (idx >= 0) expense[idx] = { ...expense[idx], ...stored };
  else expense.push(stored);
  return { income: ledger?.income || [], expense };
}

export function removePerDiemRow(ledger, repeatId) {
  return {
    income: ledger?.income || [],
    expense: (ledger?.expense || []).filter((r) => r?.repeatId !== repeatId),
  };
}

export function syncPerDiemCopies({
  row,
  sourceDateKey,
  tourDateKeys,
  previousDates,
  loadLedger,
  saveLedger,
}) {
  if (!row?.repeatId) return [];
  const nextDates = resolvePerDiemDates(row, sourceDateKey, tourDateKeys);
  const targets = perDiemSyncTargets({ row, sourceDateKey, tourDateKeys, previousDates });
  targets.forEach((key) => {
    // No loaded copy of that day: skip it. Writing an empty ledger would overwrite the day's real doc.
    const ledger = loadLedger?.(key);
    if (!ledger || typeof ledger !== 'object') return;
    if (nextDates.includes(key)) saveLedger?.(key, upsertPerDiemRow(ledger, row));
    else saveLedger?.(key, removePerDiemRow(ledger, row.repeatId));
  });
  return nextDates;
}

/** Other days a repeating row touches (new span plus any day it just left). */
export function perDiemSyncTargets({ row, sourceDateKey, tourDateKeys, previousDates }) {
  if (!row?.repeatId) return [];
  const nextDates = resolvePerDiemDates(row, sourceDateKey, tourDateKeys);
  const targets = new Set([...nextDates, ...(previousDates || [])]);
  return [...targets].filter((key) => key && key !== sourceDateKey);
}

function normalizeExpenseLabel(label) {
  return String(label || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Settlement expense lines that share a normalized label and the same non-zero amount.
 * Returns one entry per duplicate group: { label, amount, indexes }.
 */
export function findDuplicateExpenses(expenses) {
  const groups = new Map();
  (Array.isArray(expenses) ? expenses : []).forEach((line, index) => {
    const label = normalizeExpenseLabel(line?.label);
    const amount = Math.round(NUM(line?.amount) * 100) / 100;
    if (!label || !amount) return;
    const key = `${label}|${amount}`;
    const group = groups.get(key) || { label: String(line.label).trim(), amount, indexes: [] };
    group.indexes.push(index);
    groups.set(key, group);
  });
  return [...groups.values()].filter((g) => g.indexes.length > 1);
}

function csvCell(value) {
  let text = value == null ? '' : String(value);
  // Keep typed text from running as a spreadsheet formula; plain numbers pass through.
  if (/^[=+\-@\t]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * One settlement sheet as CSV text. `totals` is settleTotals(sheet);
 * `meta` is { venue, date, cityLine } for the header block.
 */
export function settlementToCsv(sheet, totals, meta = {}) {
  const st = sheet && typeof sheet === 'object' ? sheet : emptySettle();
  const t = totals && typeof totals === 'object' ? totals : settleTotals(st);
  const rows = [];
  const add = (...cells) => rows.push(cells.map(csvCell).join(','));
  const money = (n) => (Math.round(NUM(n) * 100) / 100).toFixed(2);
  add('Settlement');
  add('Venue', meta.venue || '');
  add('Date', meta.date || '');
  if (meta.cityLine) add('City', meta.cityLine);
  add('Promoter', st.promoter || '');
  add('Venue rep', st.venueRep || '');
  add('Artist rep', st.artistRep || '');
  add('Deal memo ref', st.dealRef || '');
  add('Deal', DEAL_PRESETS[st.preset]?.label || st.preset || '');
  add('Guarantee', money(st.guarantee));
  add('Split %', NUM(st.split));
  rows.push('');
  add('Ticket tier', 'Available', 'Sold', 'Price', 'Total');
  (st.tiers || []).forEach((tier) => {
    add(tier.label, NUM(tier.avail), NUM(tier.qty), money(tier.price), money(NUM(tier.qty) * NUM(tier.price)));
  });
  rows.push('');
  add('Deduction', 'Rate', 'Amount');
  add('Facility fee per paid ticket', money(st.facility), money(t.facility));
  add('Sales tax %', NUM(st.tax), money(t.tax));
  add('Credit card fees %', NUM(st.card), money(t.card));
  rows.push('');
  add('Expense', 'Amount', 'Status', 'Receipt URL');
  (st.expenses || []).forEach((x) => {
    add(x.label, money(x.amount), x.status || 'approved', x.receiptUrl || '');
  });
  rows.push('');
  add('Payment', 'Amount');
  add('Deposit', money(st.deposit));
  (st.payments || []).forEach((p) => add(p.label, money(p.amount)));
  add('Payment method for the balance', st.payMethod || '');
  rows.push('');
  add('Totals');
  add('Gross box office', money(t.gross));
  add('Net box office', money(t.net));
  add('Expenses', money(t.expenses));
  add('Artist earning', money(t.earned));
  add('Paid to date', money(t.paidToDate));
  add('Balance due', money(t.balance));
  rows.push('');
  add('Sign-off');
  add('Artist / tour manager', st.signedArtist || '');
  add('Promoter / venue', st.signedVenue || '');
  add('Date signed', st.signedDate || '');
  if (st.notes) {
    rows.push('');
    add('Notes', st.notes);
  }
  return rows.join('\r\n');
}

export function netLedgerColor(net, { positive, negative, zero } = {}) {
  const n = NUM(net);
  if (n > 0) return positive;
  if (n < 0) return negative;
  return zero;
}

/** Parse a merch catalog row from the MERCH tab structured list (title + detail lines). */
export function parseMerchCatalogItem(song, index = 0) {
  if (song?.item && typeof song.item === 'object') {
    return normalizeMerchRow({
      ...song.item,
      id: song.id || song.item.id,
      name: song.title || song.item.name,
    }, index);
  }
  const lines = Array.isArray(song?.lines) ? song.lines : [];
  const blob = lines.map((l) => (Array.isArray(l?.fields) ? l.fields : []).join(' ')).join('\n');
  const priced = String(blob).match(/\$\s*(\d+(?:\.\d+)?)/);
  const inMatch = String(blob).match(/(?:count\s*)?in\s*:?\s*(\d+)/i);
  const outMatch = String(blob).match(/(?:count\s*)?out\s*:?\s*(\d+)/i);
  const compMatch = String(blob).match(/(?:count\s*)?comp\s*:?\s*(\d+)/i);
  const addMatch = String(blob).match(/(?:count\s*)?add(?:s)?\s*:?\s*(\d+)/i);
  const photoMatch = String(blob).match(/photo\s*:?\s*(\S+)/i);
  const sellerMatch = String(blob).match(/seller\s*:?\s*([^\n]+)/i);
  const kindRaw = String((lines[0]?.fields || [])[0] || 'Apparel');
  const kind = kindRaw.replace(/\$\s*\d+(?:\.\d+)?/g, '').replace(/[—–]/g, ' ').replace(/\s+/g, ' ').trim() || 'Apparel';
  return emptyMerchRow({
    id: String(song?.id || `m-${index}`),
    name: String(song?.title || `Item ${index + 1}`).trim() || `Item ${index + 1}`,
    kind,
    price: priced ? Number(priced[1]) : 0,
    countIn: inMatch ? Number(inMatch[1]) : 0,
    countAdd: addMatch ? Number(addMatch[1]) : 0,
    countOut: outMatch ? Number(outMatch[1]) : 0,
    countComp: compMatch ? Number(compMatch[1]) : 0,
    photoUrl: photoMatch ? photoMatch[1] : '',
    seller: sellerMatch ? sellerMatch[1].trim() : 'Band',
  });
}

/** JSON blob from a published SETTLEMENT / EXPENSES tab — used when localStorage is empty. */
export function parseWorksheetSeed(raw, fallback) {
  if (raw == null || String(raw).trim() === '') return fallback;
  try {
    const o = JSON.parse(String(raw));
    if (o && typeof o === 'object') return o;
  } catch {
    /* ignore */
  }
  return fallback;
}

/** Round-trip merch count rows into the MERCH tab structured list. */
export function merchRowsToSetlistDoc(rows, merchRate = 0) {
  const opts = merchSheetOptions(merchRate);
  const list = Array.isArray(rows) ? rows : [];
  return JSON.stringify({
    v: 2,
    ...opts,
    songs: list.map((r, i) => {
      const row = normalizeMerchRow(r, i);
      return {
        id: row.id,
        title: String(row.name || '').trim() || `Item ${i + 1}`,
        open: false,
        item: row,
        lines: [
          { id: `ml-${i}-k`, fields: [String(row.kind || row.category || 'Apparel')] },
          { id: `ml-${i}-p`, fields: [`$${NUM(row.price)}`] },
          { id: `ml-${i}-in`, fields: [`in ${NUM(row.countIn)}`] },
          { id: `ml-${i}-add`, fields: [`add ${NUM(row.countAdd)}`] },
          { id: `ml-${i}-out`, fields: [`out ${NUM(row.countOut)}`] },
          { id: `ml-${i}-comp`, fields: [`comp ${NUM(row.countComp)}`] },
          { id: `ml-${i}-seller`, fields: [`seller ${String(row.seller || '')}`] },
          ...(String(row.photoUrl || '').trim()
            ? [{ id: `ml-${i}-photo`, fields: [`photo ${String(row.photoUrl).trim()}`] }]
            : []),
        ],
      };
    }),
  });
}

export function ledgerTotals(ledger) {
  const income = (ledger?.income || []).reduce((a, r) => a + NUM(r.amount), 0);
  const expense = (ledger?.expense || []).reduce((a, r) => a + (
    r?.kind === 'perdiem' || r?.kind === 'line' || r?.kind === 'dayrate' ? perDiemAmount(r) : NUM(r.amount)
  ), 0);
  return { income, expense, net: income - expense };
}
